import * as profileActions from "../../store/actions/profile";
import * as profileEvents from "../../store/events/profile";
import Box from "@mui/material/Box";
import Button from "../../components/Button/Button";
import FormSelect from "../../components/Form/FormSelect";
import FormTextField from "../../components/Form/FormTextField";
import React from "react";
import TaskHeader from "../../components/Task/TaskHeader";
import TaskTemplate from "../templates/TaskTemplate";
import { connect } from "react-redux";
import useNavigateQSA from "../../utils/hooks/useNavigateQSA";
import ErrorMessages, { ERROR_MESSAGE_TYPE_DEFAULT } from "../../components/ErrorMessages/ErrorMessages";
import {
	Form,
	isArrayOfLength,
	isObject,
	isString
} from "@pheaa/channels-component-library";
import {
	HTML_ATTR_PATTERN_ADDR_CITY_TOWN,
	HTML_ATTR_PATTERN_ADDR_POSTAL_CODE_DOMESTIC,
	HTML_ATTR_PATTERN_ADDR_POSTAL_CODE_INTERNATIONAL,
	// HTML_ATTR_PATTERN_ADDR_STATE_REGION,
	HTML_ATTR_PATTERN_ADDR_STREET,
	INTERFACE_ERROR_DCR,
	SYSTEM_ERROR_MESSAGE_400,
	getErrorMessageForCode
} from "../../config/common";

const EditAddress = ({ title, ...props }) => {

	let legalAddressObj = null;

	const {
		contactsAddressesErrors,
		contactsAddressesIsLoading,
		countryCodes,
		getCountryCodes,
		getStateCodes,
		isCountryCodesFailed,
		isCountryCodesPending,
		isStateCodesFailed,
		isStateCodesPending,
		profileData,
		setData,
		stateCodes,
		updateContactsAddresses
	} = props;

	if (isObject(profileData) && isObject(profileData.contacts) && isArrayOfLength(profileData.contacts.addresses)) {
		legalAddressObj = profileData.contacts.addresses.find(addrObj => addrObj.id === "legal");
	}

	const [selectedCountryCode, setSelectedCountryCode] = React.useState(legalAddressObj?.countryCode);
	const [selectedStateCode, setSelectedStateCode] = React.useState(legalAddressObj?.stateCode);
	const navigate = useNavigateQSA();

	const handleCountryCodeChange = e => {
		const { value } = e.target;
		setSelectedCountryCode(value);
	};

	const handleStateCodeChange = e => {
		const { value } = e.target;
		setSelectedStateCode(value);
	};

	const handleSubmit = (e, formData) => {
		let comparison = {};
		let includesChange = false;

		Object.keys(legalAddressObj).forEach((key, i) => {
			// Create comparison object from legalAddressObj, transforming string values to uppercase for comparison;
			comparison[key] = (isString(legalAddressObj[key]) ? legalAddressObj[key].toUpperCase() : legalAddressObj[key]);
		});

		Object.keys(formData).forEach((key, i) => {
			// Transform formData values to uppercase for comparison;
			formData[key] = formData[key].toUpperCase();

			if (formData[key] !== comparison[key]) {
				includesChange = true;
			}
		});

		if (includesChange) {
			updateContactsAddresses([{
				city: "",
				id: "legal",
				isValid: true,
				line1: "",
				postalCode: "",
				...formData
			}], () => {
				navigate("/profile");
			});
		} else {
			setData({ contactsAddressesErrors: [{ code: INTERFACE_ERROR_DCR, type: ERROR_MESSAGE_TYPE_DEFAULT }] });
		}
	};

	React.useEffect(() => {

		if (!isArrayOfLength(countryCodes) && !isCountryCodesFailed && !isCountryCodesPending) {
			getCountryCodes();
		}
	}, [
		countryCodes,
		getCountryCodes,
		isCountryCodesFailed,
		isCountryCodesPending
	]);

	React.useEffect(() => {

		if (!isArrayOfLength(stateCodes) && !isStateCodesFailed && !isStateCodesPending) {
			getStateCodes();
		}
	}, [
		getStateCodes,
		isStateCodesFailed,
		isStateCodesPending,
		stateCodes
	]);

	return (
		<TaskTemplate>
			<TaskHeader
				closeCallback={() => { profileEvents.clearErrors(); }}
				closeTaskDestination="/profile"
				title={title}
			/>
			<Form
				id="defaultFormNarrow"
				onSubmit={handleSubmit}
			>
				<Box mt={6}>
					<ErrorMessages
						errorMessages={contactsAddressesErrors.map(error => {
							return { ...error, message: getErrorMessageForCode(error.code, { 400: { message: SYSTEM_ERROR_MESSAGE_400 } }) };
						})}
					/>
					<Box mt={2.5}>
						<FormSelect
							label="Country"
							name="countryCode"
							onChange={handleCountryCodeChange}
							options={countryCodes.map(countryCode => ({
								label: countryCode.name,
								value: countryCode.countryCode
							}))}
							required={true}
							value={selectedCountryCode}
						/>
						<FormTextField
							name="line1"
							pattern={HTML_ATTR_PATTERN_ADDR_STREET}
							required={true}
							title="Street Address 1"
							value={legalAddressObj?.line1}
						/>
						<FormTextField
							name="line2"
							pattern={HTML_ATTR_PATTERN_ADDR_STREET}
							title="Street Address 2"
							value={legalAddressObj?.line2}
						/>
						<FormTextField
							name="city"
							pattern={HTML_ATTR_PATTERN_ADDR_CITY_TOWN}
							title="City or Town"
							value={legalAddressObj?.city}
						/>
						{
							selectedCountryCode.toUpperCase() === "US" && (
								<FormSelect
									label="State or Region"
									name="stateCode"
									onChange={handleStateCodeChange}
									options={stateCodes.map(stateCode => ({
										label: stateCode.name,
										value: stateCode.code
									}))}
									required={true}
									value={selectedStateCode}
								/>
							)
						}
						{
							/** Suppressing Foreign State/Region Code in CCPA-8520
							selectedCountryCode.toUpperCase() !== "US" && (
								<FormTextField
									name="stateCode"
									pattern={HTML_ATTR_PATTERN_ADDR_STATE_REGION}
									onChange={handleStateCodeChange}
									required={true}
									title="State or Region"
									value={selectedStateCode}
								/>
							)
							**/
						}
						<FormTextField
							name="postalCode"
							pattern={selectedCountryCode.toUpperCase() === "US" ? HTML_ATTR_PATTERN_ADDR_POSTAL_CODE_DOMESTIC : HTML_ATTR_PATTERN_ADDR_POSTAL_CODE_INTERNATIONAL}
							required={true}
							title="Zip or Postal Code"
							value={legalAddressObj?.postalCode}
						/>
						<Box
							mb={1.75}
							mt={5.625}
						>
							<Button
								disabled={contactsAddressesIsLoading}
								processing={contactsAddressesIsLoading}
								type="submit"
								variant="contained"
							>
								Save
							</Button>
						</Box>
					</Box>
				</Box>
			</Form>
		</TaskTemplate>
	);
};

const mapStateToProps = state => ({
	contactsAddressesErrors: state.profile.contactsAddressesErrors,
	contactsAddressesIsLoading: state.profile.contactsAddressesIsLoading,
	countryCodes: state.profile.countryCodes,
	isCountryCodesFailed: state.profile.isCountryCodesFailed,
	isCountryCodesPending: state.profile.isCountryCodesPending,
	isStateCodesFailed: state.profile.isStateCodesFailed,
	isStateCodesPending: state.profile.isStateCodesPending,
	profileData: state.profile.data,
	stateCodes: state.profile.stateCodes
});

const mapDispatchToProps = dispatch => ({
	getCountryCodes: data => dispatch(profileEvents.getCountryCodes(data)),
	getStateCodes: data => dispatch(profileEvents.getStateCodes(data)),
	setData: data => dispatch(profileActions.setData(data)),
	updateContactsAddresses: (data, onSuccess) => dispatch(profileEvents.updateContactsAddresses(data, onSuccess))
});

export default connect(mapStateToProps, mapDispatchToProps)(EditAddress);